import Vue from 'vue';
import Vuex from 'vuex';
import ajax from '@/ajax';
import locales from '@/models/locales';
import bookingModes from '@/models/bookingModes';
import hotelVoucherTypes from '@/models/hotelVoucherTypes';
import ResponseError from '@/models/ResponseError';

Vue.use(Vuex);

const store = new Vuex.Store({
    state: {
        privilegedClient: false,
        voucher: {},
        errors: [],
        loading: false,
        activationData: {},
        cancellationOptinResult: false,
        alternativeDatesData: {},
        proceedToBooking: false,
        success: false,
        locale: localStorage.getItem('avp-locale') || locales.english.isoCode,
        variant: localStorage.getItem('avp-variant') || ''
    },
    getters: {
        hasVoucher(state) {
            return !!Object.keys(state.voucher).length;
        },
        voucherCode(state) {
            return state.voucher.voucherCode;
        },
        isPrivilegedClient(state) {
            return state.privilegedClient;
        },
        isNonRegularHotelVoucher(state) {
            return state.activationData.hotelVoucherType === hotelVoucherTypes.externalMultiVoucher
                    || state.activationData.hotelVoucherType === hotelVoucherTypes.externalTravelVoucher
                    || state.activationData.hotelVoucherType === hotelVoucherTypes.otherExternalVoucher
                    || state.activationData.hotelVoucherType === hotelVoucherTypes.webshopAccessCode
                ;
        },
        isExternalBooking(state, getters) {
            return !getters.isNonRegularHotelVoucher && state.activationData.mode === bookingModes.externalBooking;
        },
        isExternalBookingDisclaimer(state, getters) {
            return getters.isExternalBooking && state.activationData.reservationStarted === false
        },
        isNoBooking(state, getters) {
            return !getters.isNonRegularHotelVoucher && state.activationData.mode === bookingModes.noBooking;
        },
        isHotelBooking(state, getters) {
            return !getters.isNonRegularHotelVoucher && state.activationData.mode === bookingModes.hotelBooking;
        },
        isAnimodBooking(state, getters) {
            return !getters.isNonRegularHotelVoucher && state.activationData.mode === bookingModes.animodBooking;
        },
        isBooking(state, getters) {
            return !getters.isNonRegularHotelVoucher && (getters.isAnimodBooking || getters.isHotelBooking);
        },
        isContingentExhausted(state, getters) {
            // console.log("this.isExternalBooking", getters.isExternalBooking);
            return !getters.isExternalBooking && state.activationData.backedByContingents && state.activationData.availableDates.length === 0;
        },
        isBookableHotelVoucher(state) {
            return state.voucher.bookableHotelVoucher;
        },
        systemTicketCode(state) {
            return state.voucher.systemTicketCode;
        },
        cancellationOptinResult(state) {
            return state.cancellationOptinResult;
        },
        isOrderMode(state) {
            console.log("isOrderMode:", state.voucher.voucherPortalNewsletterMode);
            return state.voucher.voucherPortalNewsletterMode === 'LastOrderStep';
        },
        lang(state) {
            return state.locale.substring(0, 2);
        },
        variant(state) {
            return state.variant || '';
        },
        singleDateSelection(state) {
            return (state.activationData.availableDates || []).length > 0;
        }
    },
    mutations: {
        setPrivilegedClient(state, privilegedClient) {
            state.privilegedClient = privilegedClient;
        },
        setVoucher(state, voucher) {
            state.voucher = voucher;
        },
        setActivationData(state, activationData) {
            state.activationData = activationData;
        },
        setCancellationOptinResult(state, cancellationOptinResult) {
            state.cancellationOptinResult = cancellationOptinResult;
        },
        setAlternativeDatesData(state, alternativeDatesData) {
            state.alternativeDatesData = alternativeDatesData;
        },
        setLocale(state, locale) {
            state.locale = locale;
        },
        setVariant(state, variant) {
            state.variant = variant;
        },
        setLoading(state, loading) {
            state.loading = loading;
        },
        setErrors(state, errors) {
            if (!Array.isArray(errors)) {
                state.errors = [errors];
            }
            else {
                state.errors = errors;
            }
        },
        setSuccess(state, success) {
            state.success = success;
        },
        setProceedToBooking(state, value) {
            state.proceedToBooking = value;
        }
    },
    actions: {
        /**
         *
         * @param {Vuex.Store} context
         * @param {Function} ajaxMethodBoundToParams
         * @param {Boolean} silent
         * @returns {Promise<*>}
         */
        async asyncAction(context, ajaxMethodBoundToParams, silent) {
            !silent && context.commit('setLoading', true);
            try {
                const { data } = await ajaxMethodBoundToParams();
                context.commit('setErrors', []);
                return data;
            }
            catch (error) {
                context.dispatch('processError', error);
                throw e;    //to be catchable outside of the action
            }
            finally {
                !silent && context.commit('setLoading', false);
            }
        },
        async setLocale(context, locale) {
            localStorage.setItem('avp-locale', locale);
            context.commit('setLocale', locale);
        },
        async setVariant(context, variant) {
            localStorage.setItem('avp-variant', variant);
            context.commit('setVariant', variant);
        },
        async setup(context) {
            const call = ajax.setup.bind(this);
            const data = await context.dispatch('asyncAction', call);
            context.commit('setPrivilegedClient', data.privilegedClient);
            return data;
        },
        async validateCode(context, { code }) {
            const call = ajax.validateCode.bind(this, code);
            const voucher = await context.dispatch('asyncAction', call);

            /*if (voucher.unknown) {
                const error =  new ResponseError("Unknown voucher", {
                    status: 404,
                    translationKey: "errors.voucher_unknown"
                });
                context.dispatch('processError', error);
                throw error;
            }*/

            context.commit('setVoucher', voucher);
            return voucher;
        },
        async fetchBookingDetails(context, code) {
            const call = ajax.fetchBookingDetails.bind(this, code, context.getters.systemTicketCode, context.getters.lang);
            const activationData = await context.dispatch('asyncAction', call);

            if (!activationData) {
                const error = new ResponseError("No activation data", {
                    status: 404,
                    translationKey: "errors.no_activation_data"
                });
                context.dispatch('processError', error);
                throw error;
            }

            context.commit('setActivationData', activationData);
        },
        async sendCancellationOptin(context, code) {
            const call = ajax.sendCancellationOptin.bind(this, code);
            const result = await context.dispatch('asyncAction', call);

            if (!result) {
                const error = new ResponseError("No activation data", {
                    status: 404,
                    translationKey: "errors.no_activation_data"
                });
                context.dispatch('processError', error);
                throw error;
            }

            context.commit('setCancellationOptinResult', result.optinStatus);
        },
        async fetchBookingDetailsToken(context, token) {
            const call = ajax.fetchBookingDetailsForToken.bind(this, token);
            const activationData = await context.dispatch('asyncAction', call);

            if (!activationData) {
                const error = new ResponseError("No activation data", {
                    status: 404,
                    translationKey: "errors.no_activation_data"
                });
                context.dispatch('processError', error);
                throw error;
            }

            context.commit('setActivationData', activationData);
        },
        async fetchBookingAlternativesForToken(context, token) {
            const call = ajax.fetchBookingAlternativesForToken.bind(this, token);
            const alternativeDatesData = await context.dispatch('asyncAction', call);
            if (!alternativeDatesData) {
                const error = new ResponseError("No alternativeDates data", {
                    status: 404,
                    translationKey: "errors.no_alternativedates_data"
                });
                context.dispatch('processError', error);
                throw error;
            }

            context.commit('setAlternativeDatesData', alternativeDatesData);
        },
        async sendBookingAlternativeSelectionForToken(context, payload) {
            const call = ajax.pickBookingAlternativeWithToken.bind(this, payload);
            return await context.dispatch('asyncAction', call);
        },
        async declineBookingAlternativesForToken(context, token) {
            const call = ajax.declineBookingAlternativesForToken.bind(this, token);
            return await context.dispatch('asyncAction', call);
        },
        async savePersonalData(context, payload) {
            const call = ajax.savePersonalData.bind(this, payload, context.getters.lang);
            const voucher = await context.dispatch('asyncAction', call);
            context.commit('setVoucher', voucher);
            return voucher;
        },
        async sendVoucherDataRequest(context, payload) {
            const call = ajax.sendVoucherDataRequest.bind(this, payload);
            return await context.dispatch('asyncAction', call);
        },
        async sendContactForm(context, payload) {
            const call = ajax.sendContactForm.bind(this, payload, context.getters.lang);
            return await context.dispatch('asyncAction', call);
        },
        async finishOrderProcess(context, payload) {
            const call = ajax.finishOrderProcess.bind(this, payload, context.getters.lang);
            const voucher = await context.dispatch('asyncAction', call);
            context.commit('setVoucher', voucher);
            return voucher;
        },
        async sendBookingRequest(context, payload) {
            const call = ajax.sendBookingRequest.bind(this, payload);
            return await context.dispatch('asyncAction', call);
        },
        async startExternalBooking(context, code) {
            const call = ajax.sendStartExternalBooking.bind(this, code, context.getters.systemTicketCode, context.getters.lang);
            const activationData = await context.dispatch('asyncAction', call);
            context.commit('setActivationData', activationData);
        },
        reset({ commit }) {
            commit('setVoucher', {});
            commit('setActivationData', {});
            commit('setCancellationOptinResult', false);
            commit('setErrors', []);
            commit('setSuccess', false);
            commit('setProceedToBooking', false);
        },
        processError(context, error) {
            context.commit('setLoading', false);
            if (error.response) {
                // client received an error response (5xx, 4xx)
                context.commit('setErrors', error.response.data);
            } else if (error.request) {
                // client never received a response, or request never left
                context.commit('setErrors', "errors.requestFailed");
            } else {
                // anything else
                console.debug( 'Error', error.message );
            }
        }
    },
    modules: {
    }
});

export default store;
